import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import BottomSheet from '../BottomSheet';
import Icon from '../Icon';
import PlainButton from '../PlainButton';
import { tokens, orange, inkA } from '../tokens';

// A BottomSheet holding one grouped list of actions plus a Cancel button
// (the design's field "⋯" sheet and confirm sheets). Items:
//   { key, label, icon?, hint?, danger?, primary?, disabled? }
// `danger` paints the row red, `primary` orange. Picking a row calls
// onSelect(key) and leaves closing to the parent, so a row can change
// itself instead (e.g. "Tap again to delete").

const ERROR_RED = '#ED4337';

const Row = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 50px;
  padding: 8px 14px;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  color: ${(p) => p.$color};

  & + & {
    border-top: 1px solid ${(p) => p.$line};
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${orange(0.08)};
  }

  &:disabled {
    opacity: 0.35;
  }
`;

const CancelButton = styled(PlainButton)`
  width: 100%;
  min-height: 50px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const ActionSheet = ({
  open,
  onClose,
  title,
  subtitle,
  items,
  onSelect,
  cancelLabel,
  zIndex,
}) => {
  const theme = useTheme();
  const t = tokens(theme);

  const colorOf = (item) => {
    if (item.danger) return ERROR_RED;
    if (item.primary) return t.primary;
    return t.text;
  };
  const iconColorOf = (item) =>
    item.danger || item.primary ? colorOf(item) : t.faint;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      label={title ? undefined : 'Actions'}
      showClose={false}
      zIndex={zIndex}
      footer={
        <CancelButton onClick={onClose} $bg={inkA(theme, 0.06)} $color={t.text}>
          {cancelLabel}
        </CancelButton>
      }
    >
      <div
        role="group"
        aria-label={title ? undefined : 'Actions'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid ' + t.border,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {items.map((item) => (
          <Row
            key={item.key}
            disabled={!!item.disabled}
            onClick={() => onSelect(item.key)}
            $color={colorOf(item)}
            $line={t.line}
          >
            {item.icon ? (
              <Icon name={item.icon} size={18} color={iconColorOf(item)} />
            ) : null}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', overflowWrap: 'anywhere' }}>
                {item.label}
              </span>
              {item.hint ? (
                <span
                  style={{
                    display: 'block',
                    marginTop: 2,
                    fontSize: 12,
                    fontWeight: 500,
                    color: t.sub,
                  }}
                >
                  {item.hint}
                </span>
              ) : null}
            </span>
          </Row>
        ))}
      </div>
    </BottomSheet>
  );
};

ActionSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.string,
      hint: PropTypes.node,
      danger: PropTypes.bool,
      primary: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string,
  zIndex: PropTypes.number,
};

ActionSheet.defaultProps = {
  title: null,
  subtitle: null,
  cancelLabel: 'Cancel',
  zIndex: 1100,
};

export default ActionSheet;
